import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990NInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990-N Filing Instructions for Nonprofit Tax Exemption"
          description="Stay tax-exempt with our comprehensive Form 990-N instructions for nonprofit organizations. Learn how to file with ease and maintain your nonprofit status."
          keywords="Form 990-N, Nonprofit organizations, 501(c)(3) organizations, Nonprofit reporting, Nonprofit compliance"
          canonical="resources/tax-exempt/form-990-n-instructions/"
          ogType="article"
          jsonLdPricing="9.90"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 990-N</span>
          </span>{' '}
          Instructions: Complete e-Postcard Filing Guide
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Complete IRS instructions to understand and file Form 990-N accurately
          on time
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Ninstructions`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 4 }}
                >
                  <Box
                    itemScope
                    itemType="https://schema.org/Article"
                    sx={{
                      p: 3,
                      px: 3,
                      backgroundColor: '#f0f5f8',
                      borderLeft: '5px solid #01456c',
                      mb: 4,
                      borderRadius: 3,
                      position: 'relative',
                    }}
                  >
                    <link
                      itemProp="mainEntityOfPage"
                      href="https://www.taxzerone.com/resources/tax-exempt/form-990-n-instructions/"
                    />

                    <div
                      itemProp="image"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                      style={{ display: 'none' }}
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>

                    <Box
                      sx={{
                        display: 'grid',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        textAlign: 'left',
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2">
                        Last updated:{' '}
                        <time
                          itemProp="datePublished"
                          dateTime="2026-07-20T09:00:00-05:00"
                        >
                          July 20, 2026
                        </time>
                        <time
                          itemProp="dateModified"
                          dateTime="2026-07-20T09:00:00-05:00"
                          style={{ display: 'none' }}
                        >
                          July 20, 2026
                        </time>
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        itemProp="author"
                        itemScope
                        itemType="https://schema.org/Person"
                      >
                        -by{' '}
                        <span itemProp="name">
                          <a
                            itemProp="url"
                            href="https://www.taxzerone.com/"
                            style={{
                              color: 'inherit',
                              textDecoration: 'none',
                            }}
                          >
                            Peter Samuel
                          </a>
                        </span>{' '}
                        | TaxZerone
                      </Typography>
                    </Box>

                    <Typography
                      variant="h4"
                      itemProp="headline"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        fontSize: { md: 20, sm: 18, xs: 16 },
                        color: '#01456c',
                        fontStyle: 'italic',
                      }}
                    >
                      Form 990-N at a Glance
                    </Typography>
                    <Typography
                      variant="body1"
                      itemProp="description"
                      sx={{ mb: 2 }}
                    >
                      <Link
                        href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=f990ez`}
                        sx={{ color: '#0202ff', textDecoration: 'underline' }}
                      >
                        IRS Form 990-N (e-Postcard)
                      </Link>{' '}
                      is an <b>electronic notice</b> for small tax-exempt
                      organizations. Organizations with{' '}
                      <b>annual gross receipts normally $50,000 or less</b>{' '}
                      generally must file Form 990-N to maintain their
                      tax-exempt status.
                    </Typography>
                    <Box
                      component="ul"
                      itemProp="articleBody"
                      sx={{
                        mb: 0,
                        pl: 0,
                        listStyle: 'none',
                        '& li': { mb: 1.6, position: 'relative', pl: 3 },
                        '& li::before': {
                          content: '"★"',
                          position: 'absolute',
                          left: 0,
                          top: '-2px',
                          color: '#01456c',
                          fontSize: '18px',
                        },
                      }}
                    >
                      <li>
                        <span>
                          <Link
                            href="/resources/tax-exempt/form-990-due-dates/"
                            sx={{
                              color: '#0202ff',
                              textDecoration: 'underline',
                            }}
                          >
                            <b>Due Date:</b>
                          </Link>{' '}
                          <b>15th day of the 5th month</b> after the end of the
                          organization&apos;s tax year (May 15 for most
                          calendar-year filers).
                        </span>
                      </li>

                      <li>
                        <span>
                          <Link
                            href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                            sx={{
                              color: '#0202ff',
                              textDecoration: 'underline',
                            }}
                          >
                            <b>Extension:</b>
                          </Link>{' '}
                          Form 990-N is <b>not eligible</b> for an extension. It
                          must be filed by the original due date.
                        </span>
                      </li>

                      <li>
                        <span>
                          <b>Late Filing:</b> There is no monetary penalty for
                          filing Form 990-N late. However, organizations that
                          fail to file a required annual return or notice for{' '}
                          <b>three consecutive years</b>{' '}
                          <Link
                            href="/resources/tax-exempt/automatic-revocation-of-tax-exempt-status/"
                            sx={{
                              color: '#0202ff',
                              textDecoration: 'underline',
                            }}
                          >
                            automatically revoke tax-exempt status
                          </Link>
                          .
                        </span>
                      </li>
                    </Box>

                    <div
                      itemProp="publisher"
                      itemScope
                      itemType="https://schema.org/Organization"
                      style={{ display: 'none' }}
                    >
                      <span itemProp="name">TaxZerone</span>

                      <div
                        itemProp="logo"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>
                    </div>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 990-N (e-Postcard)?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}>
                    Form 990-N (e-Postcard)
                  </span>{' '}
                  is an e-filing option for small tax-exempt organizations that
                  have{' '}
                  <span style={{ fontWeight: 600 }}>
                    gross receipts of $50,000 or less
                  </span>{' '}
                  in a tax year. It allows them to fulfill their annual
                  reporting requirements with the IRS easily. The form requires
                  only basic information and must be filed annually to maintain
                  their tax-exempt status.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.5,
                        }}
                      >
                        File your 990 series returns fast with TaxZerone and get
                        10% off—use code{' '}
                        <Box
                          component="strong"
                          sx={{
                            ml: 0,
                            color: '#c02652',
                            fontWeight: 600,
                            border: '2px dashed rgb(211, 163, 33)',
                            px: 1,
                            backgroundColor: 'rgb(255, 250, 205)',
                          }}
                        >
                          TZOTE10
                        </Box>{' '}
                        at checkout!
                      </Typography>
                      <Typography variant="body2">
                        We support tax years 2025, 2024, and 2023.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Ninstructions`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Must File Form 990-N?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Most small tax-exempt organizations, whose{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    gross receipts (annual income)
                  </span>{' '}
                  are usually{' '}
                  <span style={{ fontWeight: 600 }}>$50,000 or less,</span> are
                  required to{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=f990ez`}
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 990-N
                  </Link>
                  , which is an{' '}
                  <span style={{ fontWeight: 600 }}>electronic notice</span>{' '}
                  (also known as an{' '}
                  <span style={{ fontWeight: 600 }}>e-Postcard</span>). This
                  form is for organizations that are{' '}
                  <span style={{ fontWeight: 600 }}>
                    not required to file{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990
                    </Link>{' '}
                    or{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      990-EZ
                    </Link>
                  </span>{' '}
                  (which are for larger or more complex organizations).
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips :</b>{' '}
                  <span style={{ fontWeight: 600 }}>Gross receipts</span> refer
                  to the <span style={{ fontWeight: 600 }}>total income</span>{' '}
                  an organization earns during its financial year. It includes
                  all money received from various sources (such as donations,
                  grants, sales, etc.),{' '}
                  <span style={{ fontWeight: 600 }}>before any expenses</span>{' '}
                  (like operational costs, salaries, or bills) are deducted.
                </Alert>
                <Typography variant="body1">
                  An organization’s Gross receipts are $50,000 or less if the
                  organization:{' '}
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>If the organization is 1 year old or less:</b> It
                      qualifies if it received, or if donors pledged to give,
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        $75,000 or less
                      </span>{' '}
                      during its first year.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>If the organization has been 1 to 3 years:</b> It
                      qualifies if it{' '}
                      <span style={{ fontWeight: 600 }}>
                        averaged $60,000 or less
                      </span>{' '}
                      in gross receipts during its first two years.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>If the organization is 3 years:</b> It qualifies if it
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        averaged $50,000 or less
                      </span>{' '}
                      in gross receipts over the last three years, including the
                      current year.
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Due Date to File 990-N
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The due date for filing Form 990-N (e-Postcard) is the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    15th day of the 5th month
                  </span>{' '}
                  after the close of the organization&apos;s tax year. For
                  organizations following a calendar year (January 1 - December
                  31), the due date is <b>May 15th</b>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For example, if an organization&apos;s tax year ends on June
                  30th, the e-Postcard would be due on November 15th.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If the due date falls on a Saturday, Sunday or a
                  legal holiday, the due date is the next business day.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalty for Late Filing of Form 990-N (e-Postcard)
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Failing to file Form 990-N (e-Postcard) can have consequences
                  for an organization&apos;s tax-exempt status. If an eligible
                  organization fails to file the e-Postcard for three
                  consecutive years, its tax-exempt status may be automatically
                  revoked. It is crucial for organizations to stay compliant
                  with the filing requirements to maintain their tax-exempt
                  status and avoid potential penalties.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.5,
                        }}
                      >
                        E-file Form 990-N (e-Postcard) with TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Complete your Form 990-N filing with TaxZerone for the
                        tax year and stay exempt.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 220, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Ninstructions`}
                      >
                        Start E-Filing Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Information required to file Form 990-N (e-Postcard)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 990-N (e-Postcard) is a simple form that collects key
                  information about the organization, including:
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Organization&apos;s Employer identification number (EIN)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Tax year (calendar or fiscal filer)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Legal name and mailing address</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Any other names the organization uses
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Name and address of a principal officer
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Website address (if available)</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Confirmation that the organization&apos;s annual gross
                      receipts are $50,000 or less
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      A statement that the organization has terminated or is
                      going out of business (if applicable)
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 990-N (e-Postcard) with TaxZerone?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <Link
                    href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=990Ninstructions`}
                    sx={{ color: '#0202ff' }}
                    target="_blank"
                  >
                    Filing Form 990-N
                  </Link>{' '}
                  (e-Postcard) with TaxZerone is a simple and straightforward
                  process:
                </Typography>
                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Discover:</b> Enter your EIN to see your organization’s
                      information that is stored in the IRS database.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Review:</b> Review the information for accuracy and
                      make changes if needed.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Stay Exempt:</b> Choose the tax year for which you’re
                      filing and transmit your{' '}
                      <Link
                        href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=990Ninstructions`}
                        sx={{ color: '#0202ff' }}
                        target="_blank"
                      >
                        Form 990-N (e-Postcard)
                      </Link>{' '}
                      to the IRS.
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  You will receive a notification as soon as the IRS processes
                  and approves your return.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Stay exempt by e-filing Form 990-N with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File IRS Form 990-N (e-Postcard) easily with TaxZerone. Simply
              enter your EIN,
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              review your details, and submit to the IRS. The entire process
              takes only a few minutes.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              You will also get email updates on your return status as soon as
              the IRS processes it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to stay exempt?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Ninstructions`}
              >
                E-file Form 990-N
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form990NInstructions;
